import React, { FC, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { IEmbed } from "../../../../embedsV2/interface";
import { ICreateEmbed } from "../../constants";
import { parseSettings } from "../helper";
import axiosInstant from "../../../../../../../api";
import {
  updateEmbedSettings,
  getFonts,
} from "../../../../../../../api/endpoints";
import { Preview } from "./Preview";
import { ColorSelectors } from "./ColorSelectors";
import config from "../../../../../../../config";
import { Controls } from "./Controls";

interface Props {
  embed?: IEmbed;
  height?: number;
  onComplete: (data: any) => void;
  updateEmbed: (data: Partial<ICreateEmbed>) => void;
  onCreateStory?: () => void;
}

export const VisualizationSettings: FC<Props> = ({
  embed,
  height = 200,
  onComplete,
  updateEmbed,
  onCreateStory,
}) => {
  const [settings, setSettings] = useState<any>({});
  const [showPreview, setShowPreview] = useState(true);
  const [fonts, setFonts] = useState<Array<any>>([]);

  useEffect(() => {
    axiosInstant
      .get(getFonts())
      .then(({ data }) => {
        const { items = [] } = data;
        if (items?.length > 0) setFonts(items);
      })
      .catch((err) => {
        console.log((err as Error)?.message);
      });
  }, []);

  const handleSubmit = async () => {
    if (embed?.id) {
      try {
        const prevSettings = parseSettings(embed);
        const response = await axiosInstant.patch(
          updateEmbedSettings(embed.id),
          { settings: JSON.stringify({ ...prevSettings, ...settings }) }
        );

        const { status, data } = response;
        onComplete?.(data);
        if (status === 200 || status === 201)
          toast.success(`Story has been updated.`);
      } catch (error) {
        toast.error((error as Error).message);
      }
    } else onCreateStory?.();
  };

  const updateSettings = (key: string, value: string) => {
    const temp = { ...(settings || {}) };
    temp[key] = value;
    if (key === "fontIndex") {
      temp.font = fonts[parseInt(value, 10) - 1].family;
      temp.style = fonts[parseInt(value, 10) - 1].category;
    }
    setSettings(temp);
  };

  useEffect(() => {
    if (embed) {
      const res = parseSettings(embed);
      if (res) setSettings(res);
    }
  }, [embed]);

  useEffect(() => {
    updateEmbed({ colors: settings });
    setShowPreview(false);
    setTimeout(() => setShowPreview(true), 200);
  }, [settings]);

  useEffect(() => {
    if (fonts.length) {
      const div = document.createElement("div");
      div.innerHTML = fonts
        .map((item) => (
          `<link
            href="https://fonts.googleapis.com/css2?family=${item.family}&display=swap"
            rel="stylesheet"
          />`
        ))
        .join("\n");
      document.head.appendChild(div);
    }
  }, [fonts]);

  const previewLink = useMemo(
    () =>
      `${config.EMBED_HOST}/brands/${embed?.brand?.id}/${
        embed?.brand?.slug
      }-/landing/${embed?.id}/${embed?.name
        ?.replace(/ /g, "-")
        .toLowerCase()}?source=landing&preview=true`,
    [embed]
  );

  const embeddedPreviewLink = useMemo(() => {
    let settingsString = "";
    Object.entries(settings).forEach(([key, val]) => {
      const value = (val as string) || "";
      const valueString = value.includes("#") ? value.slice(1) : value;
      settingsString += `&${key}=${valueString}`;
    });
    return previewLink + settingsString;
  }, [previewLink, settings]);

  return (
    <div className="flex flex-col gap-2 justify-between h-full" style={{ height }}>
      <div className="flex w-full">
        <ColorSelectors
          settings={settings}
          onUpdate={updateSettings}
          fonts={fonts}
        />
        <Preview {...{ settings, embeddedPreviewLink, showPreview }} />
      </div>
      <Controls handleSubmit={handleSubmit} previewLink={embeddedPreviewLink} />
    </div>
  );
};
